import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { PositionModel, PositionType, Side, OptionType, GetCustomerDataResponse, GetCustomerSettingsResponse } from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { OpenContractsReportRow } from './Types';

const MMBTU_PER_CONTRACT = 10000;

interface OpenContractsReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
    customerName?: string;
}

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const OpenContractsReport: React.FC<OpenContractsReportProps> = ({
    customerData,
    defaultQuoteDate,
    customerName,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [hasRun, setHasRun] = useState<boolean>(false);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());

    // Start date defaults to next month
    const nextMonth = dayjs().add(1, 'month');
    const [startMonth, setStartMonth] = useState<number>(nextMonth.month() + 1);
    const [startYear, setStartYear] = useState<number>(nextMonth.year());

    // End date defaults to December of the next year (typical budget range)
    const [endMonth, setEndMonth] = useState<number>(12);
    const [endYear, setEndYear] = useState<number>(dayjs().year() + 1);

    const fetchPositions = async () => {
        const response = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            false,
            true,
            true,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        setPositions(response.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchPositions();
    };

    const handleDownloadExcel = async () => {
        if (reportRows.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('OpenContractsReport');

        // Column widths matching the reference workbook
        sheet.columns = [
            { width: 9.42 },  // Month
            { width: 24.14 }, // Total Company Contracts
        ];

        const lastCol = 2;

        // Row 1: Customer name - merged across both columns
        sheet.addRow([customerName ?? '']);
        sheet.mergeCells(1, 1, 1, lastCol);

        // Row 2: Report title - merged
        sheet.addRow(['Number of Open Contracts']);
        sheet.mergeCells(2, 1, 2, lastCol);

        // Row 3: Report as of date - merged
        sheet.addRow([`Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`]);
        sheet.mergeCells(3, 1, 3, lastCol);

        // Row 4: blank spacer - merged
        sheet.addRow(['']);
        sheet.mergeCells(4, 1, 4, lastCol);

        // Row 5: Header row
        sheet.addRow(['Month', 'Total Company Contracts']);

        // Data rows: column A is the first of the tenure month as a Date, column B is total contracts
        reportRows.forEach(r => {
            const [yearStr, monthStr] = r.tenureDate.split('-');
            const dateVal = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1);
            const row = sheet.addRow([dateVal, r.totalContracts]);
            row.getCell(1).numFmt = 'm/d/yy';
        });

        // Total row
        const totalRow = sheet.addRow(['Total', totalContracts]);
        totalRow.getCell(2).numFmt = '#,##0';

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `OpenContractsReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    // Group positions by tenure date and calculate contract counts
    const reportRows: OpenContractsReportRow[] = useMemo(() => {
        const openPositions = positions.filter(p => !p.isExpired);

        // Group by tenure date
        const grouped = new Map<string, PositionModel[]>();
        openPositions.forEach(p => {
            const key = p.budgetDate.year + '-' + String(p.budgetDate.month).padStart(2, '0');
            const existing = grouped.get(key) || [];
            existing.push(p);
            grouped.set(key, existing);
        });

        // Calculate totals for each month (convert MMBTUs to contracts)
        // For calls: Buy is positive, Sell is negative
        // For puts: Buy is negative, Sell is positive
        const rows: OpenContractsReportRow[] = [];
        grouped.forEach((monthPositions, key) => {
            let totalContracts = 0;

            monthPositions.forEach(p => {
                const contracts = (p.volume ?? 0) / MMBTU_PER_CONTRACT;

                if (p.positionType === PositionType.Option) {
                    if (p.optionType === OptionType.Call) {
                        // Calls: Buy is positive, Sell is negative
                        totalContracts += p.side === Side.Buy ? contracts : -contracts;
                    } else if (p.optionType === OptionType.Put) {
                        // Puts: Buy is negative, Sell is positive
                        totalContracts += p.side === Side.Buy ? -contracts : contracts;
                    }
                } else if (p.positionType === PositionType.Future) {
                    // Futures: Buy is positive, Sell is negative
                    totalContracts += p.side === Side.Buy ? contracts : -contracts;
                }
            });

            rows.push({
                tenureDate: key,
                month: dayjs(key + '-01').format('MMM-YYYY'),
                totalContracts,
                buyCallContracts: 0,
                sellCallContracts: 0,
                buyFutureContracts: 0,
            });
        });

        return rows.sort((a, b) => a.tenureDate.localeCompare(b.tenureDate));
    }, [positions]);

    const totalContracts = useMemo(() => {
        return reportRows.reduce((sum, row) => sum + row.totalContracts, 0);
    }, [reportRows]);

    // Add total row at the end
    const rowsWithTotal: OpenContractsReportRow[] = useMemo(() => {
        if (reportRows.length === 0) return [];
        return [
            ...reportRows,
            {
                tenureDate: 'zzz-total',
                month: 'Total',
                totalContracts,
                buyCallContracts: 0,
                sellCallContracts: 0,
                buyFutureContracts: 0,
            }
        ];
    }, [reportRows, totalContracts]);

    const columnDefs: ColDef<OpenContractsReportRow>[] = useMemo(() => [
        { field: 'month', headerName: 'Month', width: 120 },
        { 
            field: 'totalContracts', 
            headerName: 'Total Contracts', 
            flex: 1,
            type: 'numericColumn',
            valueFormatter: params => params.value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || ''
        },
    ], []);

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="MTM Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Download Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    <Typography variant="h5" gutterBottom>Number of Open Contracts</Typography>
                    <Typography variant="subtitle1" gutterBottom>Report as of {dayjs(quoteDate).format('MM/DD/YYYY')} close</Typography>

                    <Box sx={{ maxWidth: 300 }}>
                        <StoneXGrid
                            columns={columnDefs}
                            rows={rowsWithTotal}
                            hideGridOptions={true}
                        />
                    </Box>
                </>
            ) : null}
        </Box>
    );
};

export default OpenContractsReport;
